import React, { Component } from 'react';
import _ from 'lodash';

import './FAQDrawer.scss';
import { Button, Col, Row, Table, Tooltip } from 'grab-ui';
import Filter from 'omega-rtc/components/form/ucd/Filter';
import { Grid, Message, Popup } from 'semantic-grab-ui';
import numeral from 'numeral';
import { downloadCsv } from 'omega-rtc/utils/ucd-utils';
import {
  CHANNEL_GROUP_DISPLAY_NAMES,
  CHANNEL_GROUP_ICONS_MAP,
  FIELD_TYPE,
  FILTER_FIELDS,
  NUMBER_FORMAT,
  PROJECT_OBJECTIVE_COLUMNS_MAPPING,
  UCD_ACTION_TYPES,
  UCD_RESOURCE_NAMES,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import UCDObjectiveLabel from 'omega-rtc/components/form/ucd/common/UCDObjectiveLabel';
import MetricsDownloadCsv from 'img/ucd_project_metrics_download_csv.svg';
import NoMetrics from './NoMetrics';

import './UCDHomePage.scss';
import './ProjectDetail.scss';
import { Link } from 'react-router';
import ShowIfAuthorized from 'omega-rtc/components/form/ucd/common/ShowIfAuthorized';
import type { StoreState } from 'omega-rtc/reducers';
import { connect } from 'react-redux';
import moment from 'moment';

type Props = {
  projectMetricsView: any,
  projectID: string,
  user: Object,
};
const EARLIEST_METRICS_AVAILABLE_DATE = '2022-06-01';

class ProjectPerformancePage extends Component {
  props: Props;

  constructor(props) {
    super(props);
    this.state = {
      likedFAQs: {},
      showFeedbackMessages: {},
      showFAQFeedbackModal: false,
      selectedFaqItem: {},
    };
    this.timeoutIds = {}; // Store timeout IDs
  }

  updateFilter = (key, value) => {
    this.setState(prevState => ({
      filter: { ...prevState.filter, [key]: value },
    }));
  };

  onMyProjectChange = (isMyProjectSelected) => {
    const creator = isMyProjectSelected ? this.props.user.email : '';
    this.updateFilter('createdBy', creator);
  };

  onMoreFilterChange = filters => {
    // Clean previous state of following fields
    let moreFiltersObject = {
      channelGroup: '',
      channels: [],
      audience: [],
      start: undefined,
      end: undefined,
      creator: '',
    };

    // Reduce the filters into the moreFiltersObject format
    if (!_.isEmpty(filters)) {
      moreFiltersObject = _.reduce(filters, (obj, item) => {
        obj[item.lhs] = item.rhs;
        return obj;
      }, {});
    }

    // Update the state
    this.setState(prevState => ({
      filter: {
        ...prevState.filter,
        ...moreFiltersObject,
      },
    }));
  };

  filterCampaigns = campaigns => {
    const filter = this.state.filter;

    // Key: filter field name, Value: Filter Function for the campaign list
    const FILTER_MAP = {
      name: (campaignObj) => _.includes(campaignObj.campaign.name.toLowerCase(), filter.name.toLowerCase()),
      start: (campaignObj) => moment(campaignObj.campaign.start).isSameOrAfter(filter.start.startOf('day')),
      end: (campaignObj) => moment(campaignObj.campaign.end).isSameOrBefore(filter.end),
      verticals: (campaignObj) => campaignObj.campaign.verticals.some(vertical => filter.verticals.includes(vertical)),
      audience: (campaignObj) => filter.audience.includes(campaignObj.audience),
      channels: (campaignObj) => campaignObj.campaign.channels.includes(filter.channels),
      creator: (campaignObj) => campaignObj.createdBy === filter.creator,
      createdBy: (campaignObj) => campaignObj.createdBy === filter.createdBy,
      status: (campaignObj) => campaignObj.campaignStatus === filter.status,
      channelGroup: (campaignObj) => campaignObj.campaign.channelGroup === filter.channelGroup,
    };

    for (const filterName in FILTER_MAP) {
      if (!_.isEmpty(_.get(filter, filterName))) {
        campaigns = _.filter(campaigns, FILTER_MAP[filterName]);
      }
    }
    return campaigns;
  };

  getTableData = objectiveCols => {
    const { projectMetricsView, projectID } = this.props;
    const campaigns = _.get(projectMetricsView, 'campaigns', []);

    /**
     * Filter records
     */
    const filteredCampaigns = this.filterCampaigns(campaigns);

    /**
     * flatten records
     */
    const flattenCampaigns = _.map(filteredCampaigns, value => {
      return { ...value.metrics, ...value.campaign, ...value };
    });

    /**
     * sorted records. Default sorting by campaign's end date.
     */
    const sortedCampaigns = _.isNil(_.get(flattenCampaigns, '[0].campaign.end'))
      ? flattenCampaigns
      : _.sortBy(flattenCampaigns, obj => new Date(_.get(obj, 'campaign.end')));

    /**
     * Consider to move to common dir
     */
    const CampaignsColumn = {
      title: 'Campaigns',
      key: 'code',
      dataIndex: 'code',
      render: (value, row) => {
        const channelGroup = row.channelGroup;
        return (
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <Popup
                  trigger={
                    <img
                      src={_.get(CHANNEL_GROUP_ICONS_MAP, channelGroup)}
                      style={{
                        float: 'left',
                        marginRight: '12px',
                        width: '36px',
                        height: '36px',
                      }}
                      alt={channelGroup}
                    />
                  }
                  content={CHANNEL_GROUP_DISPLAY_NAMES[channelGroup]}
                  position="bottom center"
                  inverted
                />
                <Link to={UCD_ROUTES_LINK.CAMPAIGN_VIEW(projectID, row.campaignID)}>
                  <p style={{ marginBottom: '4px', color: '#1A1A1A' }}>
                    {row.name}
                  </p>
                </Link>
                <p
                  style={{
                    color: '#A3A3A3',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    marginTop: -5,
                  }}
                >
                  {row.code}
                </p>
              </Grid.Column>

              <Grid.Column width={4}>
                <ShowIfAuthorized
                  conditions={[
                    {
                      resource: UCD_RESOURCE_NAMES.campaign,
                      action: UCD_ACTION_TYPES.create,
                    },
                  ]}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        );
      },
    };

    return {
      source: sortedCampaigns,
      columns: [
        CampaignsColumn,
        ..._.map(objectiveCols, colConfig => {
          return {
            title: colConfig.title,
            dataIndex: colConfig.key,
            key: colConfig.key,
            render: val => {
              switch (colConfig.type) {
                case FIELD_TYPE.CURRENCY:
                  return numeral(val).format('0,0.00');
                case FIELD_TYPE.NUMBER:
                  return numeral(val).format('0,0');
                default:
                  return val;
              }
            },
            sorter: (a, b) => {
              const valueA = _.toNumber(_.get(a, colConfig.key)) || 0;
              const valueB = _.toNumber(_.get(b, colConfig.key)) || 0;

              // Compare the float values
              if (valueA < valueB) {
                return -1; // a should be placed before b
              } else if (valueA > valueB) {
                return 1; // a should be placed after b
              }
              return 0; // a and b have the same value
            },
          };
        }),
      ],
    };
  };

  // Sample input: {audience:pax, channel:sms} ,  output:  [{lhs:audience, rhs:pax},{lhs:channel, rhs:sms},....]
  convertToESFilterArray(filter) {
    return _.map(filter, (rhs, lhs) => ({ lhs, rhs }));
  }

  getObjective = () => {
    const project = _.get(this.props, 'projectView.project', {});
    const { objective, subObjective } = project;
    return [objective, subObjective].filter(x => !!x).join('.');
  };

  download = () => {
    const { projectID } = this.props;
    const tableData = this.getTableData(
      PROJECT_OBJECTIVE_COLUMNS_MAPPING[this.getObjective()],
    );
    downloadCsv(
      projectID,
      tableData.source,
      _.map(tableData.columns, row => row.key),
    );
  };

  render() {
    const projectMetricsView = _.get(this.props, 'projectMetricsView', {});
    const projectView = _.get(this.props, 'projectView', { project: {} });
    const { objective, subObjective, start } = projectView.project;
    const metrics = _.get(projectMetricsView, 'project.metrics', {});

    const isBeforeEarliestDate = start < EARLIEST_METRICS_AVAILABLE_DATE;
    const noMetricsHeaderText = isBeforeEarliestDate ? 'Metrics Not Available' : undefined;
    const noMetricsBodyText = isBeforeEarliestDate ? 'Performance metrics will not be available for projects that start before 1 June 2022' : undefined;
    const projectMetricsColumns = _.get(PROJECT_OBJECTIVE_COLUMNS_MAPPING, this.getObjective(), []);
    const tableData = this.getTableData(
      PROJECT_OBJECTIVE_COLUMNS_MAPPING[this.getObjective()],
    );

    return (
      <React.Fragment>
        <div className="project-performance-metrics">
          <Row>
            <Col span={6}>
              <UCDObjectiveLabel objective={objective} subObjective={subObjective} />
            </Col>
            {
              !_.isEmpty(tableData.source) &&
              <Col
                className="project-page-metrics-download-icon"
                span={6}
                offset={12}
              >
                <Tooltip title="Download" placement="bottom">
                  <Button onClick={this.download} type="link">
                    {' '}
                    <img alt="icon" src={MetricsDownloadCsv} />
                  </Button>
                </Tooltip>
              </Col>
            }
          </Row>
          <Row className="project-page-metrics-topbar">
            {
              _.isEmpty(metrics) ?
                <Message warning>Project-level business metrics are not available :/</Message>
                :
                <table>
                  <tr>
                    {projectMetricsColumns.map((column, index) => {
                      const value = _.get(metrics, column.key, 0);
                      const format = (column.type === FIELD_TYPE.CURRENCY
                        ? NUMBER_FORMAT.CURRENCY
                        : NUMBER_FORMAT.INTEGER);
                      const isTextBold = index === 0;
                      // for integer metrics like new_users, retained_users, we choose to round the number up
                      // this is to avoid showing 0 value to the user when the metrics value is 0.xx, since 0 value will give people the impression that the metrics is not working
                      const numValue = column.type === FIELD_TYPE.CURRENCY ? value : _.ceil(value);

                      const formattedNumber = isTextBold
                        ? (<strong><p>{numeral(numValue).format(format)}</p></strong>)
                        : (<p>{numeral(numValue).format(format)}</p>);

                      return (
                        <td key={index}>
                          <span>{column.title}</span>
                          <p>{formattedNumber}</p>
                        </td>
                      );
                    })}
                  </tr>
                </table>
            }
          </Row>
        </div>
        {(!_.isEmpty(_.get(projectMetricsView, 'campaigns', []))) && (
          <div className="campaign-performance-table">
            <Row style={{ borderTop: '1px solid #ddd', paddingTop: 10 }}>
              <div>
                <Filter
                  type="campaigns"
                  moreFiltersCallback={this.onMoreFilterChange}
                  ownershipChangeCallback={this.onMyProjectChange}
                  statusChangeCallback={(status) => this.updateFilter('status', status)}
                  verticalChangeCallback={(verticals) => this.updateFilter('verticals', verticals)}
                  searchTextChangeCallback={(campaignName) => this.updateFilter('name', campaignName)}
                  filterFields={[FILTER_FIELDS.myCampaign, FILTER_FIELDS.vertical, FILTER_FIELDS.status]}
                  moreFiltersInitialValue={this.convertToESFilterArray(_.get(this.state, 'filter', {}))}
                />
              </div>
            </Row>
            <Row>
              <Table
                dataSource={tableData.source}
                columns={tableData.columns}
              />
            </Row>
          </div>
        )}
        {(_.isEmpty(tableData.source) && _.isEmpty(metrics)) && (
          <NoMetrics headerText={noMetricsHeaderText} bodyText={noMetricsBodyText} />
        )}
      </React.Fragment>
    );
  }
}

function mapStateToProps(state: StoreState) {
  return {
    user: state.app.user,
  };
}

export default connect(mapStateToProps, {})(ProjectPerformancePage);

